import { IconChevronChevronDown } from '@doltech/icons/IconChevronChevronDown';
import { IconChevronChevronUp } from '@doltech/icons/IconChevronChevronUp';
import { Menu } from 'antd';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import * as React from 'react';

import { Typography } from '../../../DesignSystemV2/Typography';
import { Divider } from '../../../Divider/Divider';
import { Link } from '../../../Link';
import { useLandingSharedDataStore } from '../model/landing-shared-data.hook';
import { sidebarMenus } from '../model/menus/menus';
import { exploreMoreLinks } from '../model/menus/static-menus';
import { ContactInfo } from './ContactInfo/ContactInfo';
import styled from 'styled-components';

const Main = styled.div`
  .ant-menu-item.ant-menu-item-only-child {
    height: auto;
    padding-right: 0;
  }

  .ant-menu-sub.ant-menu-inline {
    background: white !important;
  }
`;

const MenuItemTextWithArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding-right: 36px;
  padding-left: 12px;
`;

interface MenuItemProps {
  className?: string;
  url?: string;
  title?: string;
  sub?: boolean;
  withDisabled?: boolean;
  categoryTitle?: boolean;
}

const MenuItem = React.memo((props: MenuItemProps) => {
  const {
    className = 'menuItem',
    sub = false,
    categoryTitle = false,
    url,
    title,
    withDisabled = false,
  } = props;
  const router = useRouter();
  const { pathname, asPath } = router;

  const renderText = React.useCallback(() => {
    if (categoryTitle) {
      return (
        <MenuItemTextWithArrow>
          <Typography.Text variant="medium/14">{title}</Typography.Text>
        </MenuItemTextWithArrow>
      );
    }
    if (sub) {
      return (
        <MenuItemTextWithArrow>
          <Typography.Text variant="regular/14">{title}</Typography.Text>
        </MenuItemTextWithArrow>
      );
    }

    return (
      <Typography.Text level={4} variant="semi-bold/16-28">
        {title}
      </Typography.Text>
    );
  }, [title, sub, categoryTitle]);

  if (url) {
    const isActiveLink = pathname.split('?')[0] === url || asPath.split('?')[0] === url;

    return (
      <Link prefetch={false} href={url} className={cl(className, { active: isActiveLink })}>
        {renderText()}
      </Link>
    );
  }

  return <span className={cl(className, { disabled: withDisabled })}>{renderText()}</span>;
});

interface LandingSideBarProps extends React.HTMLAttributes<HTMLDivElement> {
  disableHeadTag?: boolean;
}

export const LandingSideBar = observer((props: LandingSideBarProps) => {
  const { className } = props;

  const store = useLandingSharedDataStore();

  const defaultOpenIds = React.useMemo(
    () =>
      sidebarMenus
        .flatMap((s: any) => s.subs)
        .filter(Boolean)
        .map((i) => i.id),
    []
  );

  return (
    <Main>
      <Menu
        mode="inline"
        className={cl('landingSidebar', className)}
        inlineIndent={0}
        expandIcon={({ isOpen }) => {
          if (isOpen) {
            return (
              <div>
                <IconChevronChevronUp fontSize={24} color="primary100" />
              </div>
            );
          }
          return (
            <div>
              <IconChevronChevronDown fontSize={24} color="black40" />
            </div>
          );
        }}
        triggerSubMenuAction="click"
        defaultOpenKeys={defaultOpenIds}
        items={[
          ...sidebarMenus.map((menuItem, menuItemIndex) => {
            const { id, pageType, subs, title } = menuItem as any;
            const navItem = store?.getNavigationItem(pageType);

            const subNavInValid =
              subs &&
              subs?.every((item) => store?.pageLink?.staticPageLink?.[item.pageType]?.hidden);
            if (subNavInValid) {
              return null;
            }

            if (subs) {
              return {
                key: id,
                label: <MenuItem title={title} />,
                children: subs.map((subItem) => {
                  const subNavItem = store?.getNavigationItem(subItem.pageType);
                  const subItemTitle = subItem.title;
                  const isHidden = store?.pageLink?.staticPageLink?.[subItem.pageType]?.hidden;
                  if (isHidden) {
                    return null;
                  }
                  return {
                    key: subItem.id,
                    label: (
                      <MenuItem
                        className={'menuSubItem'}
                        url={subNavItem?.url}
                        title={subItemTitle}
                        withDisabled
                        categoryTitle
                      />
                    ),
                    children: subItem?.subs?.map((sub) => ({
                      key: subItem.id,
                      label: (
                        <MenuItem
                          className={'menuSubItemChild'}
                          url={store?.getNavigationItem(sub.pageType)?.url || sub?.url}
                          title={sub?.title}
                          sub
                        />
                      ),
                    })),
                  };
                }),
              };
            }
            return {
              key: id,
              label: (
                <MenuItem
                  key={id}
                  url={navItem?.url || store.pageLink?.staticPageLink?.[pageType]?.url}
                  title={title}
                  withDisabled
                />
              ),
            };
          }),

          {
            key: 'explore_more',
            label: (<MenuItem title="Tìm hiểu thêm về DOL" />) as any,
            children: exploreMoreLinks.map(({ id, url, text }) => {
              return {
                key: id,
                label: <MenuItem className={'menuSubItem'} url={url} title={text} sub />,
              };
            }),
          },
          {
            key: 'divider',
            label: <Divider.Line space={12} />,
          },
          {
            key: 'contact-info',
            label: <ContactInfo />,
          },
        ]}
      />
    </Main>
  );
});
